import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Clock, CalendarIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';
import { formatDuration } from '@/utils/youtube/formatUtils';
import { cn } from '@/lib/utils';
import { useList } from '@/contexts/ListContext';
import MuxPlayer from '@/components/mux/MuxPlayer';
import { Skeleton } from '@/components/ui/skeleton';
import { Content, ContentCategory } from '@/types/content';
import VideoSidebar from '@/components/video/VideoSidebar';
import contentfulVideoService from '@/services/contentfulVideoService';
import VimeoPlayer from '@/components/vimeo/VimeoPlayer';
import MiniPlayerWrapper from '@/components/video/MiniPlayerWrapper';
import { Badge } from '@/components/ui/badge';
import { PlayButton } from '@/components/video/PlayButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useFullscreen } from '@/hooks/useFullscreen';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
const VideoDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const {
    addToList,
    removeFromList,
    isInList
  } = useList();
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const isMobile = useIsMobile();
  const { enterFullscreen } = useFullscreen();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const {
    data: video,
    isLoading: isLoadingVideo,
    error: videoError
  } = useQuery({
    queryKey: ['video', slug],
    queryFn: () => contentfulVideoService.getVideoBySlug(slug || ''),
    enabled: !!slug
  });
  const {
    data: categories = [],
    isLoading: isLoadingCategories
  } = useQuery({
    queryKey: ['videoCategories'],
    queryFn: contentfulVideoService.getVideoCategories
  });
  const getRelatedVideos = () => {
    if (!video || !categories.length) return [];
    const allVideos: Content[] = categories.flatMap(category => category.contents);
    return allVideos.filter(v => v.id !== video.id).filter(v => {
      const videoGenre = Array.isArray(video.genre) ? video.genre : [video.genre];
      const vGenre = Array.isArray(v.genre) ? v.genre : [v.genre];
      const hasMatchingGenre = videoGenre.some(g => vGenre.includes(g));
      const videoTags = video.tags || [];
      const vTags = v.tags || [];
      const hasMatchingTags = videoTags.some(tag => vTags.includes(tag));
      return hasMatchingGenre || hasMatchingTags;
    }).slice(0, 3);
  };
  const getAllOtherVideos = () => {
    if (!video || !categories.length) return [];
    const allVideos: Content[] = categories.flatMap(category => category.contents);
    const relatedVideoIds = relatedVideos.map(v => v.id);
    return allVideos.filter(v => v.id !== video.id && !relatedVideoIds.includes(v.id));
  };
  useEffect(() => {
    if (video) {
      setIsInWatchlist(isInList(video));
    }
  }, [video, isInList]);
  const handleWatchlistToggle = () => {
    if (!video) return;
    if (isInWatchlist) {
      removeFromList(video);
      setIsInWatchlist(false);
    } else {
      addToList(video);
      setIsInWatchlist(true);
    }
  };

  // Auto-fullscreen on mobile when video page loads
  useEffect(() => {
    if (isMobile && video && videoContainerRef.current) {
      const timer = setTimeout(() => {
        enterFullscreen(videoContainerRef.current!);
      }, 500); // Small delay to ensure video is loaded
      
      return () => clearTimeout(timer);
    }
  }, [isMobile, video, enterFullscreen]);
  const primaryGenre = video && (Array.isArray(video.genre) ? video.genre[0] : video.genre);
  const relatedVideos = getRelatedVideos();
  const allOtherVideos = getAllOtherVideos();
  if (isLoadingVideo || isLoadingCategories) {
    return <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="w-full aspect-video rounded-none overflow-hidden mb-6">
            <Skeleton className="w-full h-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-8">
              <Skeleton className="h-10 w-3/4 mb-4" />
              <Skeleton className="h-6 w-1/2 mb-8" />
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="md:col-span-4">
              <Skeleton className="h-[300px] w-full" />
            </div>
          </div>
        </div>
      </Layout>;
  }
  if (videoError || !video) {
    return <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Video Not Found</h1>
          <p className="text-muted-foreground mb-8">The video you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/videos')}>
            Back to Videos
          </Button>
        </div>
      </Layout>;
  }
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const secondaryColor = resolvedTheme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const renderVideoCard = (relatedVideo: Content) => <div key={relatedVideo.id} className="cursor-pointer relative group" onClick={() => navigate(`/videos/${relatedVideo.slug || relatedVideo.id}`)}>
      <div className="aspect-[4/5] mb-2 overflow-hidden">
        <div className="relative w-full h-full">
          <img src={relatedVideo.thumbnail || relatedVideo.thumbnailUrl} alt={relatedVideo.title} className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-50" />
          <div className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-300 bg-black/50">
            <PlayButton isVisible={true} />
          </div>
        </div>
      </div>
      <h3 className="text-sm font-medium line-clamp-2 text-black">
        {relatedVideo.title}
      </h3>
    </div>;
  return <Layout>
      <div className="py-0 px-0 md:px-[16px]">
        {/* Main Video Section with Black Background */}
        <div className="bg-black">
          <div className="w-full py-[43px]">
            <div className="max-w-6xl mx-auto px-4 lg:px-8">
              <div className="w-full h-auto md:aspect-video overflow-hidden" ref={videoContainerRef}>
                <MiniPlayerWrapper title={video.title} disableOnMobile={false}>
                  {video.muxPlaybackId ? <div className={isMobile ? "w-full h-auto" : "w-full h-full"}>
                      <MuxPlayer playbackId={video.muxPlaybackId} title={video.title} className={isMobile ? "w-full h-auto" : ""} />
                    </div> : video.videoUrl ? <video src={video.videoUrl} controls className="w-full h-auto" poster={video.thumbnail || video.thumbnailUrl} /> : video.vimeoId ? <div className={isMobile ? "w-full" : "aspect-video"}>
                      <VimeoPlayer vimeoId={video.vimeoId} title={video.title} responsive={true} className="w-full" minHeight={isMobile ? "auto" : "300px"} />
                    </div> : video.videoId ? <div className="relative pb-[56.25%] h-0 overflow-hidden">
                      <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${video.videoId}`} title={video.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div> : <div className="w-full h-60 md:h-full flex items-center justify-center bg-black">
                      <p className="text-white">No video source available</p>
                    </div>}
                </MiniPlayerWrapper>
              </div>
              
              <div className="py-6">
                <h1 className="text-xl text-white mb-4">
                  {video.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-3">
                  {primaryGenre && <span className="text-sm text-gray-300">
                      {primaryGenre}
                    </span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related and More Videos Section with #DCDED5 Background */}
        {(relatedVideos.length > 0 || allOtherVideos.length > 0) && <div className="w-full" style={{
        backgroundColor: '#DCDED5'
      }}>
            <div className="max-w-6xl mx-auto px-4 lg:px-8 py-[60px]">
              {relatedVideos.length > 0 && <div className="mb-8">
                  <h2 className="text-lg text-black mb-2 px-0">Related Videos</h2>
                  {isMobile ? <div className="bleed-right-mobile">
                      <HorizontalScroller snap={true} showNextCard={false} noLeftPadding={true} disableScale={true} spacing="custom-10px">
                        {relatedVideos.map(relatedVideo => <div key={relatedVideo.id} className="flex-shrink-0 w-[72%]">
                            {renderVideoCard(relatedVideo)}
                          </div>)}
                      </HorizontalScroller>
                    </div> : <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                      {relatedVideos.map(renderVideoCard)}
                    </div>}
                </div>}

              {allOtherVideos.length > 0 && <div className="mt-8">
                  <h2 className="text-lg text-black mb-2 px-0">More Videos</h2>
                  {isMobile ? <div className="bleed-right-mobile">
                      <HorizontalScroller snap={true} showNextCard={false} noLeftPadding={true} disableScale={true} spacing="custom-10px">
                        {allOtherVideos.map(otherVideo => <div key={otherVideo.id} className="flex-shrink-0 w-[72%]">
                            {renderVideoCard(otherVideo)}
                          </div>)}
                      </HorizontalScroller>
                    </div> : <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                      {allOtherVideos.map(renderVideoCard)}
                    </div>}
                </div>}
            </div>
          </div>}
      </div>
    </Layout>;
};
export default VideoDetail;